import { c as _c } from "react/compiler-runtime";
import CustomTooltip from "@/components/utils/tooltip/CustomTooltip";
import type { MetricData } from "@/hooks/useCommunityMetrics";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import type { ReactNode } from "react";
import { formatChange, formatCompactNumber, formatNumberWithCommas, formatPercent } from "../utils/formatNumbers";
import MetricSparkline from "./MetricSparkline";
interface CumulativeMetricCardProps {
  readonly title: string;
  readonly dailyData: MetricData;
  readonly weeklyData: MetricData;
  readonly icon: ReactNode;
  readonly iconBgColor: string;
  readonly accentColor: string;
  readonly unit?: string;
  readonly href?: string;
  readonly sparklineData?: number[] | undefined;
  readonly sparklineColor?: string | undefined;
  readonly sparklineDates?: number[] | undefined;
}
function ChangeRow(t0) {
  const $ = _c(21);
  const {
    label,
    change,
    changePercent,
    unit
  } = t0;
  const isPositive = change >= 0;
  const colorClasses = isPositive ? "tw-text-green" : "tw-text-red";
  const badgeClasses = isPositive ? "tw-bg-green/20 tw-text-green" : "tw-bg-red/20 tw-text-red";
  let t1;
  if ($[0] !== label) {
    t1 = <span className="tw-text-xs tw-font-medium tw-uppercase tw-leading-none tw-tracking-wider tw-text-neutral-500">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== change) {
    t2 = formatNumberWithCommas(change);
    $[2] = change;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `${change >= 0 ? "+" : ""}${t2} ${unit}`;
  const t4 = `tw-whitespace-nowrap tw-text-right tw-text-sm tw-font-semibold tw-leading-none ${colorClasses}`;
  let t5;
  if ($[4] !== change) {
    t5 = formatChange(change);
    $[4] = change;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t6) {
    t7 = <div className="tw-flex tw-min-w-0 tw-justify-end"><CustomTooltip content={t3} placement="top">{t6}</CustomTooltip></div>;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `tw-inline-flex tw-min-w-[52px] tw-justify-center tw-rounded tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-leading-none ${badgeClasses}`;
  let t9;
  if ($[12] !== changePercent) {
    t9 = formatPercent(changePercent);
    $[12] = changePercent;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <span className={t8}>{t9}</span>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t1 || $[18] !== t10 || $[19] !== t7) {
    t11 = <div className="tw-grid tw-grid-cols-[36px_1fr_auto] tw-items-center tw-gap-2">{t1}{t7}{t10}</div>;
    $[17] = t1;
    $[18] = t10;
    $[19] = t7;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export default function CumulativeMetricCard(t0) {
  const $ = _c(59);
  const {
    title,
    dailyData,
    weeklyData,
    icon,
    iconBgColor,
    accentColor,
    unit: t1,
    href,
    sparklineData,
    sparklineColor,
    sparklineDates
  } = t0;
  const unit = t1 === undefined ? "" : t1;
  const total = dailyData.current.valueCount;
  const daily24hChange = dailyData.current.valueCount - dailyData.previous.valueCount;
  const weekly7dChange = weeklyData.current.valueCount - weeklyData.previous.valueCount;
  const t2 = `tw-group tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-800 tw-bg-[#0f1318] tw-p-5 tw-transition-all tw-duration-300 ${sparklineData && sparklineColor ? "tw-pb-0" : ""} ${href ? "hover:-tw-translate-y-1 hover:tw-border-neutral-700 hover:tw-shadow-xl hover:tw-shadow-neutral-900/50" : ""}`;
  let t3;
  if ($[0] !== href) {
    t3 = href && <ArrowTopRightOnSquareIcon className="tw-size-4 tw-text-neutral-500" />;
    $[0] = href;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t3 || $[3] !== title) {
    t4 = <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-white">{title}{t3}</h3>;
    $[2] = t3;
    $[3] = title;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-lg ${iconBgColor}`;
  let t6;
  if ($[5] !== icon || $[6] !== t5) {
    t6 = <div className={t5}>{icon}</div>;
    $[5] = icon;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <div className="tw-mb-5 tw-flex tw-items-start tw-justify-between">{t4}{t6}</div>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = `tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider ${accentColor}`;
  let t9;
  if ($[11] !== t8) {
    t9 = <p className={t8}>Total</p>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== total || $[14] !== unit) {
    t10 = unit ? `${formatNumberWithCommas(total)} ${unit}` : formatNumberWithCommas(total);
    $[13] = total;
    $[14] = unit;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== total) {
    t11 = formatCompactNumber(total);
    $[16] = total;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <p className="tw-cursor-default tw-text-3xl tw-font-bold tw-leading-none tw-text-white">{t11}</p>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12) {
    t13 = <CustomTooltip content={t10} placement="top">{t12}</CustomTooltip>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== unit) {
    t14 = unit && <p className="tw-text-xs tw-text-neutral-500">{unit}</p>;
    $[23] = unit;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t13 || $[26] !== t14 || $[27] !== t9) {
    t15 = <div className="tw-flex tw-min-w-[120px] tw-flex-col tw-gap-1">{t9}{t13}{t14}</div>;
    $[25] = t13;
    $[26] = t14;
    $[27] = t9;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="tw-hidden tw-h-full tw-w-px tw-bg-iron-700/50 xl:tw-block" />;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = `tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider ${accentColor}`;
  let t18;
  if ($[30] !== t17) {
    t18 = <p className={t17}>Changes</p>;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== daily24hChange || $[33] !== dailyData.valueCountChangePercent || $[34] !== unit) {
    t19 = <ChangeRow label="24h" change={daily24hChange} changePercent={dailyData.valueCountChangePercent} unit={unit} />;
    $[32] = daily24hChange;
    $[33] = dailyData.valueCountChangePercent;
    $[34] = unit;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== unit || $[37] !== weekly7dChange || $[38] !== weeklyData.valueCountChangePercent) {
    t20 = <ChangeRow label="7d" change={weekly7dChange} changePercent={weeklyData.valueCountChangePercent} unit={unit} />;
    $[36] = unit;
    $[37] = weekly7dChange;
    $[38] = weeklyData.valueCountChangePercent;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t18 || $[41] !== t19 || $[42] !== t20) {
    t21 = <div className="tw-flex tw-min-w-[130px] tw-flex-col tw-gap-2">{t18}{t19}{t20}</div>;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t15 || $[45] !== t21) {
    t22 = <div className="tw-grid tw-min-h-[160px] tw-grid-cols-1 tw-items-start tw-gap-4 xl:tw-grid-cols-[1fr_auto_1fr] xl:tw-gap-6">{t15}{t16}{t21}</div>;
    $[44] = t15;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== sparklineColor || $[48] !== sparklineData || $[49] !== sparklineDates) {
    t23 = sparklineData && sparklineColor && <MetricSparkline data={sparklineData} color={sparklineColor} dates={sparklineDates} />;
    $[47] = sparklineColor;
    $[48] = sparklineData;
    $[49] = sparklineDates;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t2 || $[52] !== t22 || $[53] !== t23 || $[54] !== t7) {
    t24 = <div className={t2}>{t7}{t22}{t23}</div>;
    $[51] = t2;
    $[52] = t22;
    $[53] = t23;
    $[54] = t7;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  const content = t24;
  if (href) {
    let t25;
    if ($[56] !== content || $[57] !== href) {
      t25 = <Link href={href} className="tw-group tw-block tw-no-underline hover:tw-no-underline">{content}</Link>;
      $[56] = content;
      $[57] = href;
      $[58] = t25;
    } else {
      t25 = $[58];
    }
    return t25;
  }
  return content;
}